import { useState, useCallback, useEffect, useRef } from 'react'
import { Outlet, useLocation, Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { LogOut, ChevronDown, Menu, KeyRound, User } from 'lucide-react'
import { cn } from '@/lib/utils'
import OccupantSidebar from './OccupantSidebar.jsx'
import Logo from '@/components/brand/Logo'
import AmbientBackground from '@/components/brand/AmbientBackground'
import { useAuth } from '@/data/auth'

/**
 * Layout wrapper for the occupant (renter) portal. Mirrors AppLayout's
 * structure — sidebar on desktop, drawer on mobile — but WITHOUT the
 * TenantSwitcher (occupants can never switch portfolios) and WITHOUT
 * admin controls. Everything under /portal/* should be rendered inside
 * this layout by the wire-up agent's route table.
 */

const pageTransition = {
  initial: { opacity: 0, y: 8, filter: 'blur(4px)' },
  animate: { opacity: 1, y: 0, filter: 'blur(0px)' },
  exit: { opacity: 0, y: -4, filter: 'blur(2px)' },
  transition: { duration: 0.35, ease: [0.16, 1, 0.3, 1] },
}

export default function OccupantLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const location = useLocation()

  const toggleSidebar = useCallback(() => setSidebarCollapsed((p) => !p), [])
  const closeMobile = useCallback(() => setMobileOpen(false), [])
  const openMobile = useCallback(() => setMobileOpen(true), [])

  return (
    <div className="min-h-screen bg-navy-900 relative">
      <AmbientBackground mood="default" />
      <OccupantSidebar
        collapsed={sidebarCollapsed}
        onToggle={toggleSidebar}
        mobileOpen={mobileOpen}
        onMobileClose={closeMobile}
      />

      <div
        className={cn(
          'transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] min-h-screen relative z-[1]',
          sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60',
        )}
      >
        {/* Mobile top bar */}
        <div className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-4 h-14 border-b border-white/[0.06] bg-navy-900/85 backdrop-blur-2xl">
          <button
            onClick={openMobile}
            className="p-2 -ml-2 rounded-lg text-white/70 hover:text-white hover:bg-white/[0.05] transition active:scale-95"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <Logo size={22} />
            <span className="font-display text-[14px] text-white/85">Pinnacle</span>
          </div>
          <OccupantUserMenu compact />
        </div>

        {/* Desktop user menu (no tenant switcher, no admin controls) */}
        <div className="hidden lg:flex absolute top-4 right-6 z-30 items-center gap-2">
          <OccupantUserMenu inline />
        </div>

        <main className="p-4 sm:p-5 lg:p-6">
          <AnimatePresence mode="wait">
            <motion.div key={location.pathname} {...pageTransition}>
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  )
}

function OccupantUserMenu({ compact = false, inline = false }) {
  const { user, signOut } = useAuth()
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  if (!user) return null
  const initials = (user.name || user.email).split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase()

  return (
    <div ref={ref} className={cn((compact || inline) ? 'relative' : 'absolute top-4 right-6 z-30')}>
      <button
        onClick={() => setOpen((s) => !s)}
        className={cn(
          'inline-flex items-center gap-2 rounded-full border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.05] transition',
          compact ? 'p-1' : 'pl-1 pr-3 py-1',
        )}
      >
        {user.picture ? (
          <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
        ) : (
          <span className="w-7 h-7 rounded-full grid place-items-center bg-white/[0.05] font-mono text-[10px] text-white/70">{initials}</span>
        )}
        {!compact && (
          <>
            <span className="font-mono text-[10px] text-white/65 hidden sm:inline">{user.email.split('@')[0]}</span>
            <ChevronDown className="w-3 h-3 text-white/40" />
          </>
        )}
      </button>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -8, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
          className="absolute right-0 mt-2 w-64 rounded-xl border border-white/[0.08] bg-navy-800/95 backdrop-blur-xl shadow-2xl p-2 pp-menu"
        >
          <div className="px-3 py-2 border-b border-white/[0.05] mb-1">
            <div className="text-[12px] text-white/90 leading-tight">{user.name || user.email}</div>
            <div className="font-mono text-[10px] text-white/45 truncate">{user.email}</div>
            <div className="mt-2 inline-flex items-center gap-1 font-mono text-[9px] tracking-[0.05em] uppercase px-1.5 py-0.5 rounded border bg-white/[0.04] border-white/[0.08] text-white/65">
              <User className="w-2.5 h-2.5" />
              Resident
            </div>
          </div>
          <Link
            to="/change-password"
            onClick={() => setOpen(false)}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-[12px] text-white/70 hover:text-white/95 hover:bg-white/[0.04] transition"
          >
            <KeyRound className="w-3.5 h-3.5" />
            Change password
          </Link>
          <button
            onClick={signOut}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-[12px] text-white/70 hover:text-white/95 hover:bg-rose-500/[0.08] transition"
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign out
          </button>
        </motion.div>
      )}
    </div>
  )
}
